import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

/* ─── Types ─── */
interface MemberResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  chapterAffiliation: string | null;
  userId: string | null;
}

interface UserResult {
  id: string;
  email: string;
  fullName: string;
  role: string;
  deactivatedAt: string | null;
}

interface LinkHistoryEntry {
  id: string;
  timestamp: string;
  userName: string;
  metadata: {
    memberId: string;
    memberName: string;
    memberEmail: string;
    linkedUserId: string;
    userEmail: string;
    previousRole: string;
    newRole: string;
    emailSynced: boolean;
    actions: string[];
  } | null;
}

interface LinkResult {
  success: boolean;
  member: { id: string; name: string; email: string };
  user: { id: string; email: string; role: string };
  loginPreserved: boolean;
  actions: string[];
}

interface GuestInfo {
  guestRecordId: string | null;
  ticketNumber: string | null;
  mealOption: string | null;
  tableName: string | null;
  seatNumber: string | null;
  chapterAffiliation: string | null;
  rsvpStatus: string | null;
}

/* ─── Styles ─── */
const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: '800px' },
  sectionTitle: { fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem', marginTop: '1.25rem' },
  searchInput: { width: '100%', padding: '8px 12px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' as const },
  resultCard: { background: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.5rem', cursor: 'pointer', transition: 'all 0.15s' },
  resultCardSelected: { background: '#f0f7f2', border: `2px solid ${PRIMARY}`, borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.5rem', cursor: 'pointer' },
  resultName: { fontWeight: 600, fontSize: '0.88rem', color: TEXT_DARK },
  resultMeta: { fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '2px' },
  optionRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', fontSize: '0.85rem' },
  select: { padding: '6px 10px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', fontSize: '0.82rem' },
  previewBox: { background: '#f8faf9', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '1rem', marginTop: '1rem', fontSize: '0.82rem', lineHeight: 1.7 },
  btn: { padding: '0.6rem 1.25rem', borderRadius: '8px', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
  btnPrimary: { backgroundColor: PRIMARY, color: '#fff' },
  btnSecondary: { backgroundColor: '#e5e7eb', color: '#333' },
  successBox: { background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '1rem', marginTop: '1rem' },
  errorBox: { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.5rem', fontSize: '0.82rem', color: '#991b1b' },
  historyTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.78rem', marginTop: '0.75rem' },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: `2px solid ${PRIMARY}`, fontWeight: 600, color: TEXT_MUTED, fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  td: { padding: '8px 10px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: '#333', verticalAlign: 'top' as const },
};

/* ─── Component ─── */
export function LinkSyncTab() {
  // Member search
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<MemberResult[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberResult | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // User search
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const [guestInfo, setGuestInfo] = useState<GuestInfo | null>(null);

  // Options
  const [upgradeRole, setUpgradeRole] = useState('Delegate');
  const [doUpgrade, setDoUpgrade] = useState(true);
  const [syncEmail, setSyncEmail] = useState(false);

  // State
  const [linking, setLinking] = useState(false);
  const [result, setResult] = useState<LinkResult | null>(null);
  const [error, setError] = useState('');

  // History
  const [history, setHistory] = useState<LinkHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Candidates from match suggestions
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  // Fetch link history and candidates on mount
  useEffect(() => { fetchHistory(); fetchCandidates(); }, []);

  const fetchCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const data = await apiClient<any[]>('/guest-accounts/link-candidates');
      setCandidates(data);
    } catch { /* silent */ }
    finally { setCandidatesLoading(false); }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await apiClient<LinkHistoryEntry[]>('/members/link-history');
      setHistory(data);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  };

  // Member search (debounced)
  useEffect(() => {
    if (memberSearch.trim().length < 2) { setMemberResults([]); return; }
    const timeout = setTimeout(async () => {
      setMemberLoading(true);
      try {
        const data = await apiClient<MemberResult[]>(`/members?search=${encodeURIComponent(memberSearch.trim())}`);
        // Only show unlinked members
        setMemberResults(data.filter((m) => !m.userId).slice(0, 8));
      } catch { setMemberResults([]); }
      finally { setMemberLoading(false); }
    }, 400);
    return () => clearTimeout(timeout);
  }, [memberSearch]);

  // User search (debounced)
  useEffect(() => {
    if (userSearch.trim().length < 3) { setUserResults([]); return; }
    const timeout = setTimeout(async () => {
      setUserLoading(true);
      try {
        const data = await apiClient<UserResult[]>(`/users?search=${encodeURIComponent(userSearch.trim())}`);
        setUserResults(data.filter((u) => !u.deactivatedAt).slice(0, 8));
      } catch { setUserResults([]); }
      finally { setUserLoading(false); }
    }, 400);
    return () => clearTimeout(timeout);
  }, [userSearch]);

  // Fetch guest info when user is selected
  useEffect(() => {
    if (!selectedUser) { setGuestInfo(null); return; }
    (async () => {
      try {
        // Look up guest record via the user's registration
        const data = await apiClient<any>(`/users/${selectedUser.id}/guest-info`);
        setGuestInfo(data);
      } catch {
        setGuestInfo(null);
      }
    })();
  }, [selectedUser]);

  // Execute link
  const handleLink = async () => {
    if (!selectedMember || !selectedUser) return;
    setLinking(true);
    setError('');
    setResult(null);
    try {
      const res = await apiClient<LinkResult>(`/members/${selectedMember.id}/safe-link`, {
        method: 'POST',
        body: {
          userEmail: selectedUser.email,
          upgradeRole: doUpgrade ? upgradeRole : undefined,
          syncEmail,
        },
      });
      setResult(res);
      fetchHistory(); // Refresh history
      fetchCandidates(); // Refresh candidates
    } catch (err: any) {
      setError(err?.message || 'Failed to link. Check that neither account is already linked.');
    } finally {
      setLinking(false);
    }
  };

  // Reset for another link
  const handleReset = () => {
    setSelectedMember(null);
    setSelectedUser(null);
    setMemberSearch('');
    setUserSearch('');
    setMemberResults([]);
    setUserResults([]);
    setResult(null);
    setError('');
    setDoUpgrade(true);
    setUpgradeRole('Delegate');
    setSyncEmail(false);
  };

  // Select a candidate from match suggestions (auto-fill both steps)
  const selectCandidate = (candidate: any) => {
    setSelectedMember({
      id: candidate.memberId,
      firstName: candidate.memberName.split(' ')[0] || '',
      lastName: candidate.memberName.split(' ').slice(1).join(' ') || '',
      email: candidate.memberEmail,
      chapterAffiliation: null,
      userId: null,
    });
    if (candidate.userEmail && candidate.userId) {
      setSelectedUser({
        id: candidate.userId,
        email: candidate.userEmail,
        fullName: candidate.guestName,
        role: candidate.userRole || 'Attendee',
        deactivatedAt: null,
      });
    }
  };

  // Render success state
  if (result) {
    return (
      <div style={styles.container}>
        <div style={styles.successBox}>
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem', color: '#065f46' }}>✅ Successfully Linked</div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#1a4a2e' }}>
            <div><strong>Member:</strong> {result.member.name} ({result.member.email})</div>
            <div><strong>User:</strong> {result.user.email}</div>
            <div><strong>Role:</strong> {result.user.role}</div>
            <div><strong>Login:</strong> {result.loginPreserved ? '✅ Preserved (no changes to email/password)' : '⚠️ Email was synced — login credential changed'}</div>
            {result.actions.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Actions taken:</strong>
                <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                  {result.actions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleReset}>🔗 Link Another</button>
          <BetaOptInButton guestInfo={guestInfo} />
        </div>

        {/* History always visible */}
        <LinkHistorySection history={history} loading={historyLoading} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Info Banner */}
      <div style={{ background: '#f0f7f2', border: `1px solid #c8e6c9`, borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem', fontSize: '0.83rem', color: '#1a4a2e', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>What does Link & Sync do?</div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
          <li>Connects a <strong>KDU Member record</strong> to an existing <strong>User login account</strong></li>
          <li>The user keeps their original email and password — login is never disrupted</li>
          <li>Once linked, the user appears in task assignments, subcommittee memberships, and event roles</li>
          <li>Optionally upgrade the user's role (e.g., Attendee to Delegate) to grant additional access</li>
        </ul>
      </div>

      {/* Candidates from Match Suggestions */}
      {candidates.length > 0 && !selectedMember && (
        <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#0d47a1', marginBottom: '0.5rem' }}>
            🔗 Candidates from Match Suggestions ({candidates.length})
          </div>
          <div style={{ fontSize: '0.78rem', color: '#1565c0', marginBottom: '0.5rem' }}>
            These were staged in Legacy Ball → Linking. Click one to auto-fill the form.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {candidates.map((c) => (
              <div
                key={c.suggestionId}
                onClick={() => selectCandidate(c)}
                style={{ background: '#fff', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', padding: '0.6rem 0.85rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.15s' }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: TEXT_DARK }}>{c.guestName} → {c.memberName}</div>
                  <div style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{c.userEmail || 'No user email'} • {c.ticketNumber || 'No ticket'} • {Math.round(c.confidence * 100)}% match</div>
                </div>
                {c.memberAlreadyLinked ? (
                  <span style={{ fontSize: '0.7rem', color: '#2e7d32', fontWeight: 600 }}>✅ Already linked</span>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#1565c0', fontWeight: 600 }}>Select →</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {candidatesLoading && <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: '0.75rem' }}>Loading candidates...</div>}

      {/* Step 1: Select Member */}
      <div style={styles.sectionTitle}>Step 1: Select Member (unlinked only)</div>
      {!selectedMember ? (
        <>
          <input
            style={styles.searchInput}
            placeholder="Search members by name..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            aria-label="Search members"
          />
          {memberLoading && <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '4px' }}>Searching...</div>}
          {memberResults.map((m) => (
            <div key={m.id} style={styles.resultCard} onClick={() => { setSelectedMember(m); setMemberResults([]); setMemberSearch(''); }}>
              <div style={styles.resultName}>{m.firstName} {m.lastName}</div>
              <div style={styles.resultMeta}>{m.email} • {m.chapterAffiliation || 'No chapter'}</div>
            </div>
          ))}
        </>
      ) : (
        <div style={styles.resultCardSelected}>
          <div style={styles.resultName}>✓ {selectedMember.firstName} {selectedMember.lastName}</div>
          <div style={styles.resultMeta}>{selectedMember.email} • {selectedMember.chapterAffiliation || 'No chapter'}</div>
          <button style={{ ...styles.btn, ...styles.btnSecondary, marginTop: '0.5rem', padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setSelectedMember(null)}>Change</button>
        </div>
      )}

      {/* Step 2: Select User */}
      <div style={styles.sectionTitle}>Step 2: Select User Account</div>
      {!selectedUser ? (
        <>
          <input
            style={styles.searchInput}
            placeholder="Search by email or name (min 3 chars)..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            aria-label="Search user accounts"
          />
          {userLoading && <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '4px' }}>Searching...</div>}
          {userResults.map((u) => (
            <div key={u.id} style={styles.resultCard} onClick={() => { setSelectedUser(u); setUserResults([]); setUserSearch(''); }}>
              <div style={styles.resultName}>{u.fullName || u.email}</div>
              <div style={styles.resultMeta}>{u.email} • Role: {u.role}</div>
            </div>
          ))}
        </>
      ) : (
        <div style={styles.resultCardSelected}>
          <div style={styles.resultName}>✓ {selectedUser.fullName || selectedUser.email}</div>
          <div style={styles.resultMeta}>{selectedUser.email} • Role: {selectedUser.role}</div>
          <button style={{ ...styles.btn, ...styles.btnSecondary, marginTop: '0.5rem', padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setSelectedUser(null)}>Change</button>
        </div>
      )}

      {/* Step 3: Options */}
      {selectedMember && selectedUser && (
        <>
          <div style={styles.sectionTitle}>Step 3: Options</div>
          <div style={styles.optionRow}>
            <label>
              <input type="checkbox" checked={doUpgrade} onChange={(e) => setDoUpgrade(e.target.checked)} />
              {' '}Upgrade role to:
            </label>
            <select style={styles.select} value={upgradeRole} onChange={(e) => setUpgradeRole(e.target.value)} disabled={!doUpgrade}>
              <option value="Attendee">Attendee</option>
              <option value="Delegate">Delegate</option>
              <option value="Organizer">Organizer</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
          <div style={styles.optionRow}>
            <label>
              <input type="checkbox" checked={syncEmail} onChange={(e) => setSyncEmail(e.target.checked)} />
              {' '}Sync login email to member email
            </label>
          </div>
          {syncEmail && selectedMember.email !== selectedUser.email && (
            <div style={{ ...styles.errorBox, background: '#fef3c7', borderColor: '#fbbf24', color: '#92400e' }}>
              ⚠️ This will change the login email from <strong>{selectedUser.email}</strong> to <strong>{selectedMember.email}</strong>. The user will need to log in with the new email.
            </div>
          )}

          {/* Preview */}
          <div style={styles.previewBox}>
            <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Preview:</div>
            <div>• <strong>member.userId</strong> → will be set to {selectedUser.email}</div>
            {doUpgrade && (
              <div>• <strong>user.role</strong> → {selectedUser.role} → {upgradeRole}</div>
            )}
            <div>• <strong>user.fullName</strong> → "{selectedMember.firstName} {selectedMember.lastName}"</div>
            <div>• <strong>Login email:</strong> {syncEmail && selectedMember.email !== selectedUser.email
              ? `⚠️ CHANGED to ${selectedMember.email}`
              : `✅ UNCHANGED (${selectedUser.email})`}</div>
            <div>• <strong>Password:</strong> ✅ UNCHANGED</div>

            {guestInfo && (
              <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}` }}>
                <div style={{ fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.8rem' }}>Guest Record Details:</div>
                <div>• <strong>Ticket:</strong> {guestInfo.ticketNumber || 'None'}</div>
                <div>• <strong>Meal:</strong> {guestInfo.mealOption || 'Not selected'}</div>
                <div>• <strong>Table:</strong> {guestInfo.tableName || 'Unassigned'}{guestInfo.seatNumber ? ` (Seat ${guestInfo.seatNumber})` : ''}</div>
                <div>• <strong>Chapter:</strong> {guestInfo.chapterAffiliation || 'None'}</div>
                <div>• <strong>RSVP:</strong> {guestInfo.rsvpStatus || 'N/A'}</div>
              </div>
            )}
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary, opacity: linking ? 0.6 : 1 }}
              onClick={handleLink}
              disabled={linking}
            >
              {linking ? 'Linking...' : '🔗 Link & Sync'}
            </button>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={handleReset}>Cancel</button>
          </div>
        </>
      )}

      {/* Link History */}
      <LinkHistorySection history={history} loading={historyLoading} />
    </div>
  );
}

/* ─── Beta Opt-In Button Sub-Component ─── */
function BetaOptInButton({ guestInfo }: { guestInfo: GuestInfo | null }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  // Conditions: guest must have a guest record ID and a ticket number
  const eligible = !!(guestInfo?.guestRecordId && guestInfo?.ticketNumber);

  const handleOptIn = async () => {
    if (!guestInfo?.guestRecordId) return;
    setStatus('loading');
    try {
      await apiClient('/beta-testing/opt-in-from-link', { method: 'POST', body: { guestRecordId: guestInfo.guestRecordId } });
      setStatus('done');
      setMsg('Added to beta testing whitelist');
    } catch (e: any) {
      const errMsg = e?.message || 'Failed to opt in';
      if (errMsg.includes('already')) {
        setStatus('done');
        setMsg('Already in beta whitelist');
      } else {
        setStatus('error');
        setMsg(errMsg);
      }
    }
  };

  if (status === 'done') {
    return <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600 }}>🧪 ✅ {msg || 'Beta Whitelisted'}</span>;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <button
        onClick={handleOptIn}
        disabled={!eligible || status === 'loading'}
        title={eligible ? 'Add this user to the beta testing whitelist' : 'User needs a guest record with a ticket number to be eligible'}
        style={{
          ...styles.btn,
          backgroundColor: eligible ? '#7c3aed' : '#d1d5db',
          color: eligible ? '#fff' : '#9ca3af',
          cursor: eligible ? 'pointer' : 'not-allowed',
          opacity: status === 'loading' ? 0.6 : 1,
          padding: '0.5rem 1rem',
        }}
      >
        {status === 'loading' ? '⏳ Adding...' : '🧪 Opt into Beta'}
      </button>
      {status === 'error' && <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>{msg}</span>}
    </span>
  );
}

/* ─── Link History Sub-Component ─── */
function LinkHistorySection({ history, loading }: { history: LinkHistoryEntry[]; loading: boolean }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [snapshotGuest, setSnapshotGuest] = useState<GuestInfo | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const handleExpand = async (entry: LinkHistoryEntry) => {
    if (expandedId === entry.id) {
      setExpandedId(null);
      setSnapshotGuest(null);
      return;
    }
    setExpandedId(entry.id);
    setSnapshotGuest(null);
    if (entry.metadata?.linkedUserId) {
      setSnapshotLoading(true);
      try {
        const data = await apiClient<GuestInfo>(`/users/${entry.metadata.linkedUserId}/guest-info`);
        setSnapshotGuest(data);
      } catch { setSnapshotGuest(null); }
      finally { setSnapshotLoading(false); }
    }
  };

  if (loading) return <div style={{ marginTop: '2rem', color: TEXT_MUTED, fontSize: '0.82rem' }}>Loading history...</div>;
  if (history.length === 0) return null;

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ ...styles.sectionTitle, marginTop: 0 }}>📋 Link History ({history.length})</div>
      <table style={styles.historyTable}>
        <thead>
          <tr>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Member</th>
            <th style={styles.th}>User Email</th>
            <th style={styles.th}>New Email</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Linked By</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry) => {
            const loginEmail = entry.metadata?.emailSynced ? entry.metadata?.memberEmail : entry.metadata?.userEmail;
            const memberName = entry.metadata?.memberName || 'Member';
            const message = `Hi ${memberName.split(' ')[0]}, your KDU-HUB account has been activated! Your username is: ${loginEmail}. Your password is your ticket number (e.g. TKT-042). Log in at https://kdu-hub-web.up.railway.app/login and you'll be prompted to set a new password. See you at the reunion!`;
            const isExpanded = expandedId === entry.id;

            return (
              <React.Fragment key={entry.id}>
                <tr>
                  <td style={styles.td}>{new Date(entry.timestamp).toLocaleDateString()}</td>
                  <td style={styles.td}>{entry.metadata?.memberName || '—'}</td>
                  <td style={styles.td}>{entry.metadata?.userEmail || '—'}</td>
                  <td style={styles.td}>
                    {entry.metadata?.emailSynced ? entry.metadata?.memberEmail || '—' : '—'}
                  </td>
                  <td style={styles.td}>
                    {entry.metadata?.previousRole !== entry.metadata?.newRole
                      ? `${entry.metadata?.previousRole} → ${entry.metadata?.newRole}`
                      : entry.metadata?.newRole || '—'}
                  </td>
                  <td style={styles.td}>{entry.userName}</td>
                  <td style={styles.td}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 4px', borderRadius: '4px' }}
                      title="View snapshot"
                      aria-label={`View snapshot for ${memberName}`}
                      onClick={() => handleExpand(entry)}
                    >
                      📸
                    </button>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 4px', borderRadius: '4px' }}
                      title="Copy welcome message"
                      aria-label={`Copy welcome message for ${memberName}`}
                      onClick={() => { navigator.clipboard.writeText(message); alert('Copied!'); }}
                    >
                      📋
                    </button>
                  </td>
                </tr>
                {isExpanded && entry.metadata && (
                  <tr>
                    <td colSpan={7} style={{ padding: '0.75rem 1rem', background: '#f8faf9', borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.78rem', lineHeight: 1.8 }}>
                        <div>
                          <div style={{ fontWeight: 700, marginBottom: '0.3rem', color: TEXT_DARK }}>Member Record</div>
                          <div>Name: <strong>{entry.metadata.memberName}</strong></div>
                          <div>Member Email: {entry.metadata.memberEmail}</div>
                          <div>Member ID: <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{entry.metadata.memberId}</span></div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, marginBottom: '0.3rem', color: TEXT_DARK }}>User Account</div>
                          <div>Login Email: <strong>{entry.metadata.userEmail}</strong></div>
                          {entry.metadata.emailSynced && <div>Changed to: <strong>{entry.metadata.memberEmail}</strong></div>}
                          <div>Role: {entry.metadata.previousRole} → <strong>{entry.metadata.newRole}</strong></div>
                          <div>User ID: <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{entry.metadata.linkedUserId}</span></div>
                        </div>
                      </div>
                      {entry.metadata.actions && entry.metadata.actions.length > 0 && (
                        <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}`, fontSize: '0.75rem' }}>
                          <div style={{ fontWeight: 700, marginBottom: '0.2rem', color: TEXT_DARK }}>Actions Performed:</div>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: TEXT_MUTED }}>
                            {entry.metadata.actions.map((a, i) => <li key={i}>{a}</li>)}
                          </ul>
                        </div>
                      )}
                      <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}`, fontSize: '0.75rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.2rem', color: TEXT_DARK }}>Guest Record Details:</div>
                        {snapshotLoading && <div style={{ color: TEXT_MUTED }}>Loading...</div>}
                        {!snapshotLoading && snapshotGuest && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 1rem', color: TEXT_MUTED }}>
                            <div>Ticket: <strong>{snapshotGuest.ticketNumber || 'None'}</strong></div>
                            <div>Meal: <strong>{snapshotGuest.mealOption || 'Not selected'}</strong></div>
                            <div>Table: <strong>{snapshotGuest.tableName || 'Unassigned'}{snapshotGuest.seatNumber ? ` (Seat ${snapshotGuest.seatNumber})` : ''}</strong></div>
                            <div>Chapter: <strong>{snapshotGuest.chapterAffiliation || 'None'}</strong></div>
                          </div>
                        )}
                        {!snapshotLoading && !snapshotGuest && <div style={{ color: TEXT_MUTED }}>No guest record found</div>}
                      </div>
                      <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: `1px solid ${BORDER_LIGHT}`, fontSize: '0.75rem', color: TEXT_MUTED }}>
                        Linked on {new Date(entry.timestamp).toLocaleString()} by {entry.userName}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
